/**
 * Self-Relay Service
 * 
 * Handles the self-relay flow for Gravity → ETH transfers:
 * 1. User submits MsgSendToEth on Gravity Bridge
 * 2. User calls MsgRequestBatch to create a batch
 * 3. Wait for validators to sign the batch
 * 4. Get batch calldata from backend
 * 5. Submit batch to Gravity Bridge contract on Ethereum
 */

import { api } from './api';
import { GRAVITY_BRIDGE_CONTRACT } from './bridge-messages';
import type { EthProvider } from './eth-bridge';

// ============================================================================
// Types
// ============================================================================

/**
 * Self-relay state machine states
 */
export enum SelfRelayState {
  /** Initial state */
  IDLE = 'IDLE',
  /** SendToEth transaction is pending */
  SEND_TO_ETH_PENDING = 'SEND_TO_ETH_PENDING',
  /** RequestBatch transaction is pending */
  REQUEST_BATCH_PENDING = 'REQUEST_BATCH_PENDING',
  /** Waiting for transfer to be included in a batch */
  WAITING_FOR_BATCH = 'WAITING_FOR_BATCH',
  /** Waiting for validators to sign the batch */
  WAITING_FOR_SIGNATURES = 'WAITING_FOR_SIGNATURES',
  /** Fetching batch calldata from backend */
  FETCHING_CALLDATA = 'FETCHING_CALLDATA',
  /** User is submitting the batch to Ethereum */
  SUBMITTING_TO_ETH = 'SUBMITTING_TO_ETH',
  /** Ethereum transaction is pending */
  ETH_TX_PENDING = 'ETH_TX_PENDING',
  /** Waiting for Ethereum transaction confirmation */
  WAITING_ETH_CONFIRMATION = 'WAITING_ETH_CONFIRMATION',
  /** Relay completed successfully */
  COMPLETE = 'COMPLETE',
  /** Relay failed */
  ERROR = 'ERROR',
}

/**
 * Progress information for the self-relay flow
 */
export interface SelfRelayProgress {
  state: SelfRelayState;
  message: string;
  batchNonce?: number;
  ethTxHash?: string;
  error?: string;
}

/**
 * Batch info from pending sends v2 endpoint
 */
export interface BatchInfo {
  batch_nonce: number;
  batch_timeout: number;
  token_contract: string;
  tx_count: number;
}

/**
 * Pending send with batch information
 */
export interface PendingSendV2 {
  sender: string;
  eth_dest: string;
  amount: {
    denom: string;
    amount: string;
  };
  bridge_fee: {
    denom: string;
    amount: string;
  };
  tx_id: number;
  batch_info: BatchInfo | null;
}

/**
 * Response from pending-sends-v2 endpoint
 */
export interface PendingSendsV2Response {
  unbatched: PendingSendV2[];
  batched: PendingSendV2[];
}

/**
 * Batch status response from backend
 */
export interface BatchStatusResponse {
  batch_nonce: number;
  exists: boolean;
  has_signatures: boolean;
  timed_out: boolean;
  token_contract: string | null;
  tx_count: number | null;
  message: string;
}

/**
 * Batch calldata response from backend
 */
export interface BatchCalldataResponse {
  batch_nonce: number;
  calldata: string;
  gravity_contract: string;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Fetch pending sends with batch information
 */
export async function fetchPendingSendsV2(address: string): Promise<PendingSendsV2Response> {
  return api.get<PendingSendsV2Response>(`/bridge/pending-sends-v2/${address}`);
}

/**
 * Check batch status (exists, has signatures, not timed out)
 */
export async function checkBatchStatus(batchNonce: number): Promise<BatchStatusResponse> {
  return api.get<BatchStatusResponse>(`/bridge/batch-status/${batchNonce}`);
}

/**
 * Fetch batch calldata for submission to Ethereum
 * Returns 400 if batch not ready (no signatures, timed out, etc.)
 */
export async function fetchBatchCalldata(batchNonce: number): Promise<BatchCalldataResponse> {
  return api.get<BatchCalldataResponse>(`/bridge/batch-calldata/${batchNonce}`);
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Sleep for a given number of milliseconds
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Calculate exponential backoff delay with jitter
 */
function getBackoffDelay(attempt: number, baseDelay = 2000, maxDelay = 30000): number {
  // Exponential backoff: baseDelay * 2^attempt
  const delay = Math.min(baseDelay * Math.pow(2, attempt), maxDelay);
  // Add jitter: ±20%
  const jitter = delay * 0.2 * (Math.random() - 0.5);
  return Math.floor(delay + jitter);
}

// ============================================================================
// Main Self-Relay Functions
// ============================================================================

/**
 * Find the batch nonce for a user's transfer
 * 
 * Polls the pending-sends-v2 endpoint until the transfer is included in a batch.
 * Returns the batch_nonce when found.
 * 
 * @param gravityAddress User's gravity address
 * @param txId Transaction ID of the SendToEth message (optional, for filtering)
 * @param onProgress Callback for progress updates
 * @param maxAttempts Maximum polling attempts (default 60 = ~5 minutes with backoff)
 */
export async function waitForBatch(
  gravityAddress: string,
  txId?: number,
  onProgress?: (progress: SelfRelayProgress) => void,
  maxAttempts = 60
): Promise<number> {
  onProgress?.({
    state: SelfRelayState.WAITING_FOR_BATCH,
    message: 'Waiting for transfer to be included in a batch...',
  });

  let attempt = 0;
  while (attempt < maxAttempts) {
    try {
      const response = await fetchPendingSendsV2(gravityAddress);
      
      // Look for our transfer in the batched list
      let batchedTransfer: PendingSendV2 | undefined;
      
      if (txId !== undefined) {
        // If we have a specific txId, find it
        batchedTransfer = response.batched.find(tx => tx.tx_id === txId);
      } else {
        // Otherwise, take the first batched transfer
        batchedTransfer = response.batched[0];
      }
      
      if (batchedTransfer?.batch_info) {
        const batchNonce = batchedTransfer.batch_info.batch_nonce;
        onProgress?.({
          state: SelfRelayState.WAITING_FOR_BATCH,
          message: `Transfer found in batch #${batchNonce}`,
          batchNonce,
        });
        return batchNonce;
      }
      
      // Not batched yet, wait and retry
      const delay = getBackoffDelay(attempt, 3000, 15000);
      onProgress?.({
        state: SelfRelayState.WAITING_FOR_BATCH,
        message: `Transfer not batched yet. Retrying in ${Math.round(delay / 1000)}s... (attempt ${attempt + 1}/${maxAttempts})`,
      });
      
      await sleep(delay);
      attempt++;
    } catch (error) {
      console.error('Error checking batch status:', error);
      
      // Don't fail immediately on network errors, keep retrying
      const delay = getBackoffDelay(attempt);
      await sleep(delay);
      attempt++;
    }
  }
  
  throw new Error('Timed out waiting for transfer to be included in a batch');
}

/**
 * Wait for batch to have sufficient signatures
 * 
 * The backend's /batch-calldata endpoint will return an error if the batch
 * doesn't have enough signatures. We use exponential backoff to poll until ready.
 * 
 * @param batchNonce Batch nonce to wait for
 * @param onProgress Callback for progress updates  
 * @param maxAttempts Maximum polling attempts (default 120 = ~10 minutes with backoff)
 */
export async function waitForBatchSignatures(
  batchNonce: number,
  onProgress?: (progress: SelfRelayProgress) => void,
  maxAttempts = 120
): Promise<BatchCalldataResponse> {
  onProgress?.({
    state: SelfRelayState.WAITING_FOR_SIGNATURES,
    message: `Waiting for validators to sign batch #${batchNonce}...`,
    batchNonce,
  });

  let attempt = 0;
  while (attempt < maxAttempts) {
    try {
      // Try to fetch calldata - this will succeed once signatures are ready
      const calldata = await fetchBatchCalldata(batchNonce);
      
      onProgress?.({
        state: SelfRelayState.FETCHING_CALLDATA,
        message: 'Batch is ready for relay!',
        batchNonce,
      });
      
      return calldata;
    } catch (error) {
      // Check if this is a 400 error (batch not ready yet)
      const errMsg = error instanceof Error ? error.message : 'Unknown error';
      
      // Check if batch has timed out
      if (errMsg.toLowerCase().includes('timed out')) {
        throw new Error(`Batch #${batchNonce} has timed out on Ethereum`);
      }
      
      // Wait with exponential backoff
      const delay = getBackoffDelay(attempt, 5000, 30000);
      onProgress?.({
        state: SelfRelayState.WAITING_FOR_SIGNATURES,
        message: `Waiting for signatures... (attempt ${attempt + 1}/${maxAttempts})`,
        batchNonce,
      });
      
      await sleep(delay);
      attempt++;
    }
  }
  
  throw new Error(`Timed out waiting for batch #${batchNonce} signatures`);
}

/**
 * Ethereum mainnet chain ID
 */
const ETHEREUM_MAINNET_CHAIN_ID_HEX = '0x1';

/**
 * Switch MetaMask to Ethereum mainnet
 */
async function switchToEthereumMainnet(provider: EthProvider): Promise<void> {
  try {
    await provider.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: ETHEREUM_MAINNET_CHAIN_ID_HEX }],
    });
  } catch (error) {
    // Error code 4902 means the chain hasn't been added yet (shouldn't happen for mainnet)
    const err = error as { code?: number };
    if (err.code === 4902) {
      throw new Error('Ethereum mainnet not available in wallet');
    }
    throw error;
  }
}

/**
 * Submit batch to Ethereum using the user's wallet
 * 
 * @param provider Ethereum provider (MetaMask, etc.)
 * @param from User's Ethereum address
 * @param calldata Hex-encoded calldata string or BatchCalldataResponse object
 * @param onProgress Callback for progress updates
 */
export async function submitBatchToEthereum(
  provider: EthProvider,
  from: string,
  calldata: string | BatchCalldataResponse,
  onProgress?: (progress: SelfRelayProgress) => void
): Promise<string> {
  // Handle both string calldata and BatchCalldataResponse object
  const calldataStr = typeof calldata === 'string' ? calldata : calldata.calldata;
  const gravityContract = typeof calldata === 'string' ? GRAVITY_BRIDGE_CONTRACT : (calldata.gravity_contract || GRAVITY_BRIDGE_CONTRACT);
  const batchNonce = typeof calldata === 'string' ? undefined : calldata.batch_nonce;

  onProgress?.({
    state: SelfRelayState.SUBMITTING_TO_ETH,
    message: 'Switching to Ethereum network...',
    batchNonce,
  });

  // Switch to Ethereum mainnet before submitting
  await switchToEthereumMainnet(provider);

  onProgress?.({
    state: SelfRelayState.SUBMITTING_TO_ETH,
    message: 'Please confirm the transaction in your wallet...',
    batchNonce,
  });

  // Ensure calldata has 0x prefix
  const data = calldataStr.startsWith('0x') 
    ? calldataStr 
    : `0x${calldataStr}`;

  // Send the transaction
  const txHash = await provider.request({
    method: 'eth_sendTransaction',
    params: [{
      from,
      to: gravityContract,
      data,
      // Gas limit is estimated by the wallet
    }],
  }) as string;

  onProgress?.({
    state: SelfRelayState.WAITING_ETH_CONFIRMATION,
    message: 'Transaction submitted. Waiting for confirmation...',
    batchNonce,
    ethTxHash: txHash,
  });

  return txHash;
}

/**
 * Wait for Ethereum transaction confirmation
 * 
 * @param provider Ethereum provider
 * @param txHash Transaction hash to wait for
 * @param confirmations Number of confirmations to wait for (default 1)
 */
export async function waitForEthConfirmation(
  provider: EthProvider,
  txHash: string,
  confirmations = 1,
  onProgress?: (progress: SelfRelayProgress) => void
): Promise<{ success: boolean; blockNumber: number }> {
  const maxAttempts = 120; // 10 minutes with 5 second intervals
  let attempt = 0;

  while (attempt < maxAttempts) {
    const receipt = await provider.request({
      method: 'eth_getTransactionReceipt',
      params: [txHash],
    });

    if (receipt !== null) {
      const receiptObj = receipt as {
        blockNumber: string;
        status: string;
      };
      const blockNumber = parseInt(receiptObj.blockNumber, 16);
      const success = receiptObj.status === '0x1';

      if (!success) {
        onProgress?.({
          state: SelfRelayState.ERROR,
          message: 'Transaction reverted',
          ethTxHash: txHash,
          error: 'Transaction was reverted on Ethereum',
        });
        return { success: false, blockNumber };
      }

      if (confirmations <= 1) {
        onProgress?.({
          state: SelfRelayState.COMPLETE,
          message: 'Self-relay completed successfully!',
          ethTxHash: txHash,
        });
        return { success: true, blockNumber };
      }

      // Wait for additional confirmations
      const latestBlock = await provider.request({
        method: 'eth_blockNumber',
        params: [],
      }) as string;
      const currentBlock = parseInt(latestBlock, 16);

      if (currentBlock - blockNumber >= confirmations - 1) {
        onProgress?.({
          state: SelfRelayState.COMPLETE,
          message: 'Self-relay completed successfully!',
          ethTxHash: txHash,
        });
        return { success: true, blockNumber };
      }
    }

    await sleep(5000);
    attempt++;
  }

  throw new Error('Transaction confirmation timed out');
}

// ============================================================================
// Complete Self-Relay Flow
// ============================================================================

/**
 * Execute the complete self-relay flow
 * 
 * This is the main entry point for self-relay after the user has:
 * 1. Submitted MsgSendToEth
 * 2. Submitted MsgRequestBatch
 * 
 * @param gravityAddress User's gravity address
 * @param ethProvider Ethereum provider
 * @param ethAddress User's Ethereum address
 * @param txId Optional transaction ID to track specific transfer
 * @param onProgress Callback for progress updates
 */
export async function executeSelfRelay(
  gravityAddress: string,
  ethProvider: EthProvider,
  ethAddress: string,
  txId?: number,
  onProgress?: (progress: SelfRelayProgress) => void
): Promise<{ success: boolean; ethTxHash?: string }> {
  try {
    // Step 1: Wait for transfer to be included in a batch
    const batchNonce = await waitForBatch(gravityAddress, txId, onProgress);

    // Step 2: Wait for signatures and get calldata
    const calldata = await waitForBatchSignatures(batchNonce, onProgress);

    // Step 3: Submit to Ethereum
    const txHash = await submitBatchToEthereum(
      ethProvider,
      ethAddress,
      calldata,
      onProgress
    );

    // Step 4: Wait for confirmation
    const result = await waitForEthConfirmation(
      ethProvider,
      txHash,
      1,
      onProgress
    );

    return {
      success: result.success,
      ethTxHash: txHash,
    };
  } catch (error) {
    const errMsg = error instanceof Error ? error.message : 'Unknown error';
    onProgress?.({
      state: SelfRelayState.ERROR,
      message: `Self-relay failed: ${errMsg}`,
      error: errMsg,
    });
    throw error;
  }
}

/**
 * Self-relay service object for module-level access
 */
export const selfRelayService = {
  fetchPendingSendsV2,
  checkBatchStatus,
  fetchBatchCalldata,
  waitForBatch,
  waitForBatchSignatures,
  submitBatchToEthereum,
  waitForEthConfirmation,
  executeSelfRelay,
};

/**
 * Alias for waitForBatch - finds the batch nonce for a user's transfer
 * @deprecated Use waitForBatch instead
 */
export const findBatchNonceForUser = waitForBatch;
